import { OnAnimationFrame } from "../../library-js/animation/OnAnimationFrame";
import { InputManager } from "../../library-js/dom-tools/InputManager";
import { TemplatesManager, TemplatesManagerMode } from "../../library-js/templates/TemplatesManager";
import { Insight } from "../dom-tools/Insight";
import { PageHandler, PageHandlerMode } from "../dom-tools/PageHandler";
import { UserAgentDeviceTypes } from "../dom/UserAgentDeviceType";
import { ActivityAnalyser } from "../tools/ActivityAnalyser";
import { LandscapeScale } from "../dom-tools/LandscapeScale";
import { AppPathConverter } from "./AppPathConverter";
import { CookieSettings } from "../../library/ts/html/cookies/CookieSettings";
import { nextFrame } from "../animation/nextFrame";
import { DateMath } from "../date/DateMath";
import { ElementType } from "../dom-tools/ElementType";
import { DOMOrientation } from "../dom/DOMOrientation";
import { ClassFlag } from "../dom-tools/ClassFlag";

export class AppInitializerData
{
  localAdress:string;
  webAdress:string;
  templatesList:string[];
  pageRoot:string;
  disableForceHTTPS?:boolean;
  htmlMode?:boolean;
  allowWWWSubdomain?:boolean;
}

export class ElectronAppInitializerData
{
  appLocation:string;
  templatesList:string[];
  pageRoot:string;
  disableForceHTTPS?:boolean;
  htmlMode?:boolean;
  allowWWWSubdomain:boolean;
}

export class App
{
  protected _cookieSettings:CookieSettings;
  
  readonly activityAnalyser = new ActivityAnalyser();
  readonly onAnimationFrame = new OnAnimationFrame();
  readonly inputManager = new InputManager();
  readonly templatesManager = new TemplatesManager();
  readonly insight = new Insight();
  readonly landscapeScale = new LandscapeScale();
  readonly pathConverter = new AppPathConverter( this );

  private _pageHandler:PageHandler;
  private _initializerData:AppInitializerData;
  private _electronInitializerData:ElectronAppInitializerData;

 
  protected _loaded = false;
  get loaded()
  {
    return this._loaded;
  }

  protected _loadedTime:Date = null;

  get timeElapsedSinceLoaded()
  {
    return DateMath.getDifferenceMs( new Date(), this._loadedTime ) / 1000;
  } 

  processRawTemplateElement( element:Element )
  {
    element = this.templatesManager.processElement( element );
    this.pageHandler.rootPathResolver.process( element, this.pageHandler.currentPage );
    return element;
  }

  resolveRootPaths( element:Element )
  {
    let wrapper:Element = null;

    if ( ! element.parentElement )
    {
      wrapper = ElementType.div.create();
      wrapper.appendChild( element );
    }

    this.pageHandler.rootPathResolver.process( element.parentElement, this.pageHandler.currentPage );

    if ( wrapper )
    {
      wrapper.removeChild( element );
    }
  }

  createTemplate( nodeName:string )
  { 
    let element = this.templatesManager.createTemplate( nodeName );
    this.pageHandler.rootPathResolver.process( element, this.pageHandler.currentPage );
    return element;
  }

  setLoaded()
  {
    this._loaded = true;
    this._loadedTime = new Date();
  }

  get initializerData(){ return this._initializerData; }

  get pageHandler(){ return this._pageHandler; }

  
  get webAdress()
  {
    return this._initializerData.webAdress;
  }

  get isHTMLMode()
  {
    return this._initializerData.htmlMode === true;
  }

  grabHash()
  {
    let hash = window.location.hash;
    return hash;
  }

  get isLocal()
  {
    let url = window.location + "";
    return ! url.startsWith( "https://" );
  }   

  async initializeApp( data:AppInitializerData|ElectronAppInitializerData ):Promise<void>
  {    
    if ( document.readyState === "loading" )
    {
      document.addEventListener( 
        "DOMContentLoaded", 
        ()=>
        { 
          this.setLoaded();
        } 
      ); 
    }
    else
    {
      this.setLoaded();
    }

    setTimeout( ()=>{ this.setLoaded() }, 3000 )

    if ( ! data.allowWWWSubdomain  )
    { 
      let url = window.location + "";

      if ( url.startsWith( "https://www." ) )
      {
        let realURL = url.replace( /^https:\/\/www\./, "https://" );

        window.location.assign( realURL );

        return Promise.resolve();
      }
    }

    UserAgentDeviceTypes.setOnBody();

    if ( ( data as  ElectronAppInitializerData ).appLocation )
    {
      console.log( "ELECTRON APP" );
      this._electronInitializerData = data as ElectronAppInitializerData;

    }
    else
    {
      this._initializerData = data as AppInitializerData;
    }
    

    if ( data.templatesList )
    {
      let templatesList = data.templatesList;
      this.templatesManager.setMode( TemplatesManagerMode.IGNORE_STYLES );
      templatesList.forEach( c => this.templatesManager.addTemplateHTML( c ) );          
    }
    
    if ( this._initializerData )
    {
      //console.log( "PAGE HANDLER FOR WEB" );
      let appData = this._initializerData;
      this._pageHandler = new PageHandler( appData.localAdress, appData.webAdress, data.disableForceHTTPS );
    }
    else
    {
      //console.log( "PAGE HANDLER FOR APP" );
      let electronData = this._electronInitializerData;
      this._pageHandler = new PageHandler( electronData.appLocation, null, electronData.disableForceHTTPS, PageHandlerMode.ELECTRON );
    }
    

    this.pageHandler.setPageRootTag( data.pageRoot );

    
    
    this.onAnimationFrame.run();    

    this._isPortrait = DOMOrientation.isPortrait;
    this.updateOrientationFlags();


    this.onAnimationFrame.addListener(
      ()=>
      {
        this.landscapeScale.update();
        this.insight.update();

        if ( this._isPortrait !== DOMOrientation.isPortrait )
        {
          this._isPortrait = DOMOrientation.isPortrait;
          this.updateOrientationFlags();          
        }
        
      } 
    ) 

    this.activityAnalyser.start();

    await this.pageHandler.initialize();

    this.initializePage();    

  }

  _isPortrait:boolean;

  updateOrientationFlags( )
  {
    new ClassFlag( "LANDSCAPE" ).setAs( document.body, ! this._isPortrait );
    new ClassFlag( "PORTRAIT" ).setAs( document.body, this._isPortrait );
  }

  async initializePage()
  {
    while ( ! this.loaded )
    {
      await nextFrame();
    }

    //console.log( "App.initializePage" );
    this.insight.grabElements();
    this.templatesManager.processChildren( document.body );
    this.pageHandler.replaceLinks();

    return Promise.resolve();
  }

  static updateAll()
  {
    Insight.updateAll();
  }

  static _appOverlayLogType = new ElementType( "app--overlay--log");
  static _logLine = new ElementType( "log--line");
  static _maxLogs = 8;

  static logInfo( ...params:any[] )
  {
    App._logWithType( "info", params );
  }

  static logError( ...params:any[] )
  {
    App._logWithType( "error", params );
  }

  static logWarn( ...params:any[] )
  {
    App._logWithType( "warn", params );
  }

  static _logWithType( type:string, params:any[] )
  {
    let element =  App._appOverlayLogType.queryDoc();
    
    if ( ! element )
    {
      element = App._appOverlayLogType.create( "" );
      document.documentElement.appendChild( element );
    }

    if ( element.childNodes.length > App._maxLogs )
    {
      element.removeChild( element.firstChild );
    }

    let line = App._logLine.create( params.join( " " ) );
    ClassFlag.assign( line, type );

    element.appendChild( line );
    
    if ( type === "error" )
    {
      console.error( line );
    }
    else if ( type === "warn" )
    {
      console.warn( line );
    }
    else if ( type === "info" )
    {
      console.log( line );
    }
    

  }
}